const MAX_SAFE_LABEL_LENGTH = 160;
const SESSION_MANAGEMENT_ID_PATTERN = /^[A-Za-z0-9_-]{43}$/;

export const canManageAccountSessions = (store) => (
  store?.isAuth === true && store?.accountSessionManagementAvailable === true
);

const normalizedText = (value, maxLength = MAX_SAFE_LABEL_LENGTH) => {
  if (typeof value !== "string") {
    return null;
  }
  const text = value.trim();
  if (!text) {
    return null;
  }
  return text.slice(0, maxLength);
};

export const isValidSessionManagementId = (value) => (
  typeof value === "string" && SESSION_MANAGEMENT_ID_PATTERN.test(value)
);

export const sanitizeAccountSession = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  if (!isValidSessionManagementId(value.sessionManagementId)) {
    return null;
  }

  const sessionManagementId = value.sessionManagementId;

  return {
    sessionManagementId,
    applicationLabel: normalizedText(value.applicationLabel),
    clientType: normalizedText(value.clientType, 40),
    deviceLabel: normalizedText(value.deviceLabel),
    createdAt: normalizedText(value.createdAt, 64),
    lastActiveAt: normalizedText(value.lastActiveAt, 64),
    expiresAt: normalizedText(value.expiresAt, 64),
    current: value.current === true,
    approximateLocation: normalizedText(value.approximateLocation),
  };
};

export const extractAccountSessions = (responseData) => {
  const candidates = Array.isArray(responseData?.data?.sessions)
    ? responseData.data.sessions
    : responseData?.sessions;

  if (!Array.isArray(candidates)) {
    return [];
  }

  return candidates
    .map(sanitizeAccountSession)
    .filter(Boolean)
    .sort((left, right) => {
      if (left.current !== right.current) {
        return left.current ? -1 : 1;
      }
      return String(right.lastActiveAt || "").localeCompare(String(left.lastActiveAt || ""));
    });
};

export const accountSessionFailureReason = (responseData) => {
  const reason = normalizedText(responseData?.reason || responseData?.data?.reason, 80);
  if (reason) {
    return reason.toLowerCase();
  }

  const code = normalizedText(responseData?.code || responseData?.data?.code, 80);
  const normalizedCode = code ? code.toUpperCase() : "";
  if (["SESSION_REVOKED", "PASSWORD_CHANGED"].includes(normalizedCode)) {
    return "session_revoked";
  }
  if (["ACCOUNT_DISABLED", "ACCOUNT_ACCESS_REVOKED"].includes(normalizedCode)) {
    return "account_disabled";
  }
  if (["ACCOUNT_DELETED", "ACCOUNT_DELETION_PENDING"].includes(normalizedCode)) {
    return "account_deleted";
  }
  return null;
};

export const shouldEndCurrentWebSession = ({responseData, actionIncludedCurrent = false}) => {
  const reason = accountSessionFailureReason(responseData);
  if (["session_revoked", "account_disabled", "account_deleted"].includes(reason)) {
    return true;
  }
  if (responseData?.mandatoryPurge === true) {
    return true;
  }
  return actionIncludedCurrent && responseData?.success !== false;
};

export const accountSessionErrorMessage = (responseData, fallback) => (
  normalizedText(responseData?.message || responseData?.data?.message, 240) || fallback
);

export const accountSessionClientLabel = (clientType) => {
  const normalized = String(clientType || "").trim().toLowerCase();
  if (normalized === "windows") return "Windows application";
  if (normalized === "ios") return "iOS application";
  if (normalized === "android") return "Android application";
  if (["browser", "web"].includes(normalized)) return "Browser";
  return "Application";
};

export const formatAccountSessionTime = (value) => {
  if (!value) {
    return "Unavailable";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unavailable";
  }
  return new Intl.DateTimeFormat(undefined, {
    timeZone: 'America/New_York',
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};
